import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { api } from '../lib/api'
import { User, Order } from '../types'
import { useAuth } from '../contexts/AuthContext'
import { useStore } from '../contexts/StoreContext'
import { getCache, getCacheStale, setCache } from '../lib/cache'
import { showOfflineToast } from '../lib/toastManager'
import {
  User as UserIcon,
  MapPin,
  Phone,
  Mail,
  ShoppingBag,
  Store,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff,
  Building2,
  Camera,
  Trash2,
  History,
  RotateCcw,
  Users,
  Briefcase,
  UserCircle,
  Shield,
  Download,
  FileText
} from 'lucide-react'
import ImageEditor from '../components/ImageEditor'
import InstallPWA from '../components/InstallPWA'

interface ProfileData extends User {
  store?: any
  stores?: any[]
  employee_info?: {
    store_id?: string
    store_name?: string
    store_slug?: string
    employee_type?: string
    is_active?: boolean
    hired_at?: string
  }
}

const Profile = () => {
  const navigate = useNavigate()
  const { viewMode, setViewMode, user: authUser, isEmployee, isCustomer, logout } = useAuth()
  const { stores } = useStore() // Usar stores del contexto en lugar de cargar directamente
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [changingPassword, setChangingPassword] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [activeTab, setActiveTab] = useState<'profile' | 'orders' | 'store'>('profile')

  const [profile, setProfile] = useState<ProfileData | null>(null)
  const [orders, setOrders] = useState<Order[]>([])
  const [storeOwnerRequest, setStoreOwnerRequest] = useState<any>(null)
  const [editing, setEditing] = useState(false)
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })

  const [editForm, setEditForm] = useState({
    name: '',
    phone: ''
  })
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const [photoPreview, setPhotoPreview] = useState<string | null>(null)
  const [photoSignedUrl, setPhotoSignedUrl] = useState<string | null>(null)
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null)
  const [showImageEditor, setShowImageEditor] = useState(false)
  const [photoHistory, setPhotoHistory] = useState<any[]>([])
  const [showHistoryModal, setShowHistoryModal] = useState(false)
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [showPhotoModal, setShowPhotoModal] = useState(false)

  // Refs para prevenir ejecuciones duplicadas (React StrictMode)
  const loadingProfileRef = useRef(false)
  const loadingOrdersRef = useRef(false)

  useEffect(() => {
    // Prevenir ejecuciones duplicadas causadas por React StrictMode
    if (loadingProfileRef.current) return
    loadingProfileRef.current = true

    const loadData = async () => {
      await loadProfile()
      if (activeTab === 'orders' && !loadingOrdersRef.current) {
        loadingOrdersRef.current = true
        await loadOrders()
        loadingOrdersRef.current = false
      }
    }

    loadData().finally(() => {
      loadingProfileRef.current = false
    })
  }, [activeTab])

  // Actualizar stores en el perfil cuando cambien en el contexto
  useEffect(() => {
    if (authUser?.type === 'store_owner' && stores.length > 0 && profile) {
      const updatedProfile = { ...profile, stores: stores }
      setProfile(updatedProfile)
      // Actualizar cache
      const cacheKey = `profile_${authUser.id}`
      setCache(cacheKey, updatedProfile, 10 * 60 * 1000)
    }
  }, [stores, authUser?.id, authUser?.type])

  // Cargar URL firmada de la foto de perfil cuando cambie el perfil
  useEffect(() => {
    if (profile?.profilePhotoUrl) {
      loadPhotoSignedUrl(profile.profilePhotoUrl)
    } else {
      setPhotoSignedUrl(null)
    }
  }, [profile?.profilePhotoUrl])

  const loadPhotoSignedUrl = async (photoKey: string) => {
    try {
      const response = await api.get(`/upload/signed-url/${encodeURIComponent(photoKey)}`)
      if (response.data.success && response.data.data?.signedUrl) {
        setPhotoSignedUrl(response.data.data.signedUrl)
      }
    } catch (error) {
      console.error('Error loading photo signed URL:', error)
    }
  }

  const handleDeleteAccount = async (confirmation: string) => {
    try {
      setSaving(true)
      const response = await api.deleteAccount(confirmation)
      
      if (response.success) {
        toast.success('Tu cuenta ha sido eliminada exitosamente')
        // Logout and redirect
        logout()
        setTimeout(() => {
          window.location.href = '/'
        }, 2000)
      }
    } catch (error: any) {
      console.error('Error deleting account:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar cuenta. Verifica que no tengas colmados activos o pedidos pendientes.')
    } finally {
      setSaving(false)
    }
  }

  const loadStoreOwnerRequest = async () => {
    try {
      const response = await api.get('/auth/my-store-owner-request')
      setStoreOwnerRequest(response.data.data)
    } catch (error) {
      console.error('Error loading store owner request:', error)
    }
  }

  const loadProfile = async () => {
    if (!authUser) return
    
    const cacheKey = `profile_${authUser.id}`
    
    // Intentar cargar desde cache primero
    const cachedProfile = getCacheStale<any>(cacheKey)
    if (cachedProfile) {
      setProfile(cachedProfile)
      setLoading(false)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      const response = await api.getProfile()
      // Mapear profile_photo_url del backend a profilePhotoUrl del frontend
      // Mapear is_active del backend a isActive del frontend
      const profileData: ProfileData = {
        ...response.data,
        profilePhotoUrl: response.data.profile_photo_url || response.data.profilePhotoUrl,
        isActive: response.data.is_active !== undefined ? response.data.is_active : (response.data.isActive !== undefined ? response.data.isActive : true),
        employee_info: response.data.employee_info || undefined
      }
      setProfile(profileData)
      
      // Guardar perfil base en cache
      setCache(cacheKey, profileData, 10 * 60 * 1000)

      // Para dueños de negocio, usar stores del StoreContext en lugar de cargar directamente
      // Esto elimina la query duplicada
      if (response.data.type === 'store_owner' && stores.length > 0) {
        const updatedProfile = { ...profileData, stores: stores }
        setProfile(updatedProfile)
        // Actualizar cache con stores del contexto
        setCache(cacheKey, updatedProfile, 10 * 60 * 1000)
      } else if (response.data.type === 'store_owner') {
        // Si no hay stores en el contexto aún, usar los del cache si existen
        if (cachedProfile && cachedProfile.stores) {
          setProfile(prev => prev ? { ...prev, stores: cachedProfile.stores } : null)
        } else {
          // Si no hay stores en contexto ni cache, dejar vacío (se cargarán cuando StoreContext se inicialice)
          setProfile(prev => prev ? { ...prev, stores: [] } : null)
        }
      }

      // Cargar estado de solicitud de dueño para clientes
      if (response.data.type === 'customer') {
        await loadStoreOwnerRequest()
      }
    } catch (error: any) {
      console.error('Error loading profile:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedProfile) {
        showOfflineToast() // Usar gestor centralizado (no muestra toast, solo log)
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      toast.error('Error al cargar el perfil')
    } finally {
      setLoading(false)
    }
  }

  const loadOrders = async () => {
    if (!authUser) return
    
    const cacheKey = `profile_orders_${authUser.id}`
    
    // Intentar cargar desde cache primero
    const cachedOrders = getCacheStale<any[]>(cacheKey)
    if (cachedOrders && cachedOrders.length > 0) {
      setOrders(cachedOrders)
      // Si tenemos cache válido, no hacer query HTTP (evitar 304 innecesario)
      // Solo hacer query si el cache está expirado o no existe
      return
    }
    
    try {
      // Usar el endpoint general de orders que funciona para todos los tipos de usuario
      const response = await api.get('/orders')
      if (response.data.success) {
        // Normalizar las órdenes para asegurar que todos los campos estén disponibles
        const normalizedOrders = (response.data.data || []).map((order: any) => ({
          ...order,
          totalAmount: Number(order.total_amount || order.totalAmount) || 0,
          deliveryFee: Number(order.delivery_fee || order.deliveryFee) || 0,
          taxAmount: Number(order.tax_amount || order.taxAmount) || 0,
          createdAt: order.created_at || order.createdAt || new Date().toISOString(),
          status: order.status || 'pending',
          items: order.items || []
        }))
        setOrders(normalizedOrders)
        
        // Guardar en cache (5 minutos de TTL)
        setCache(cacheKey, normalizedOrders, 5 * 60 * 1000)
      } else {
        // Solo borrar si no hay cache
        if (!cachedOrders || cachedOrders.length === 0) {
          setOrders([])
        }
      }
    } catch (error: any) {
      console.error('Error loading orders:', error)
      
      // Si hay error de red y tenemos datos en cache, mantenerlos
      if ((error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) && cachedOrders) {
        // Ya tenemos los datos del cache, no hacer nada más
        return
      }
      
      // Si es error 400, probablemente el usuario no tiene pedidos o no es customer
      if (error.response?.status !== 400) {
        toast.error('Error al cargar los pedidos')
      }
      
      // Solo borrar si no hay cache
      if (!cachedOrders || cachedOrders.length === 0) {
        setOrders([])
      }
    }
  }

  const handleEdit = () => {
    if (profile) {
      setEditForm({
        name: profile.name,
        phone: profile.phone || ''
      })
    }
    setEditing(true)
  }

  const handleCancelEdit = () => {
    setEditing(false)
    setEditForm({ name: '', phone: '' })
  }

  const handleSaveProfile = async () => {
    if (!editForm.name.trim()) {
      toast.error('El nombre es obligatorio')
      return
    }

    setSaving(true)
    try {
      await api.updateProfile({
        name: editForm.name.trim(),
        phone: editForm.phone.trim()
      })

      toast.success('Perfil actualizado exitosamente')
      setEditing(false)
      await loadProfile() // Recargar datos del perfil
    } catch (error: any) {
      console.error('Error updating profile:', error)
      toast.error(error.response?.data?.message || 'Error al actualizar el perfil')
    } finally {
      setSaving(false)
    }
  }

  const handleRequestStoreOwner = () => {
    navigate('/request-store-owner')
  }

  const handleJoinStore = () => {
    navigate('/join-store')
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen')
      return
    }

    // Validar tamaño (máximo 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 5MB')
      return
    }

    // Cerrar modal de foto si está abierto
    setShowPhotoModal(false)

    // Guardar archivo y mostrar editor
    setSelectedImageFile(file)
    setShowImageEditor(true)
    
    // Limpiar el input
    event.target.value = ''
  }

  const handleSaveEditedImage = async (editedFile: File) => {
    setShowImageEditor(false)
    setUploadingPhoto(true)
    
    try {
      // Subir imagen editada
      const formData = new FormData()
      formData.append('image', editedFile)
      formData.append('folder', 'users')

      const uploadResponse = await api.post('/upload/image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })

      if (uploadResponse.data.success && uploadResponse.data.data?.key) {
        // Actualizar perfil con la nueva foto
        await api.updateProfilePhoto(uploadResponse.data.data.key)
        toast.success('Foto de perfil actualizada exitosamente')
        setSelectedImageFile(null)
        await loadProfile() // Recargar perfil
        await loadPhotoHistory() // Recargar historial
      } else {
        throw new Error('Error al subir la imagen')
      }
    } catch (error: any) {
      console.error('Error uploading photo:', error)
      toast.error(error.response?.data?.message || 'Error al subir la foto de perfil')
      setSelectedImageFile(null)
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleCancelImageEdit = () => {
    setShowImageEditor(false)
    setSelectedImageFile(null)
  }

  const handleDeletePhoto = async () => {
    if (!confirm('¿Estás seguro de que quieres eliminar tu foto de perfil? Esta acción eliminará la foto permanentemente.')) {
      return
    }

    setUploadingPhoto(true)
    try {
      await api.deleteProfilePhoto()
      toast.success('Foto de perfil eliminada exitosamente')
      setPhotoPreview(null)
      setPhotoSignedUrl(null)
      await loadProfile() // Recargar perfil
      await loadPhotoHistory() // Recargar historial
    } catch (error: any) {
      console.error('Error deleting photo:', error)
      toast.error(error.response?.data?.message || 'Error al eliminar la foto de perfil')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const loadPhotoHistory = async () => {
    try {
      setLoadingHistory(true)
      const response = await api.getProfilePhotoHistory()
      if (response.success && response.data) {
        // Cargar URLs firmadas para cada foto del historial
        const historyWithUrls = await Promise.all(
          response.data.map(async (item: any) => {
            try {
              const urlResponse = await api.get(`/upload/signed-url/${encodeURIComponent(item.photo_url)}`)
              return {
                ...item,
                signedUrl: urlResponse.data.success ? urlResponse.data.data?.signedUrl : null
              }
            } catch {
              return { ...item, signedUrl: null }
            }
          })
        )
        setPhotoHistory(historyWithUrls)
      }
    } catch (error) {
      console.error('Error loading photo history:', error)
    } finally {
      setLoadingHistory(false)
    }
  }

  const handleRestorePhoto = async (historyId: string) => {
    try {
      setUploadingPhoto(true)
      await api.restoreProfilePhoto(historyId)
      toast.success('Foto de perfil restaurada exitosamente')
      await loadProfile()
      await loadPhotoHistory()
      setShowHistoryModal(false)
    } catch (error: any) {
      console.error('Error restoring photo:', error)
      toast.error(error.response?.data?.message || 'Error al restaurar la foto de perfil')
    } finally {
      setUploadingPhoto(false)
    }
  }

  const handleShowHistory = () => {
    setShowHistoryModal(true)
    loadPhotoHistory()
  }

  const handleChangePassword = async () => {
    if (!passwordData.currentPassword) {
      toast.error('Ingresa tu contraseña actual')
      return
    }

    if (!passwordData.newPassword) {
      toast.error('Ingresa la nueva contraseña')
      return
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres')
      return
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Las contraseñas no coinciden')
      return
    }

    setSaving(true)
    try {
      const response = await api.put('/users/profile/password', {
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword
      })

      if (response.data.success) {
        toast.success('Contraseña actualizada exitosamente')
        setChangingPassword(false)
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      } else {
        toast.error(response.data.message || 'Error al cambiar la contraseña')
      }
    } catch (error: any) {
      console.error('Error changing password:', error)
      const errorMessage = error.response?.data?.message || 'Error al cambiar la contraseña'
      toast.error(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-yellow-600 bg-yellow-100'
      case 'preparing': return 'text-blue-600 bg-blue-100'
      case 'ready': return 'text-purple-600 bg-purple-100'
      case 'delivering': return 'text-orange-600 bg-orange-100'
      case 'delivered': return 'text-green-600 bg-green-100'
      case 'cancelled': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pendiente'
      case 'preparing': return 'Preparando'
      case 'ready': return 'Listo'
      case 'delivering': return 'En camino'
      case 'delivered': return 'Entregado'
      case 'cancelled': return 'Cancelado'
      default: return status
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando perfil...</p>
        </div>
      </div>
    )
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl">😕</div>
          <h2 className="mt-4 text-xl font-semibold text-gray-900">Error al cargar perfil</h2>
          <p className="mt-2 text-gray-600">No se pudo cargar la información del perfil</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50/50 pb-20 sm:pb-6">
      {/* Header Sticky */}
      <div className="bg-white/80 backdrop-blur-2xl border-b border-gray-200/60 shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8">
          <div className="py-3 sm:py-4 md:py-6">
            <div className="flex items-center gap-2 sm:gap-3 md:gap-4">
                <button
                  onClick={() => setShowPhotoModal(true)}
                className="relative w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 rounded-xl sm:rounded-2xl md:rounded-3xl overflow-hidden bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-lg ring-2 ring-white hover:ring-primary-300 transition-all duration-200 hover:scale-105 cursor-pointer flex-shrink-0"
                  title="Ver y editar foto de perfil"
                >
                  {photoSignedUrl || photoPreview ? (
                    <img
                      src={photoPreview || photoSignedUrl || ''}
                      alt={profile.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                  <UserIcon className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-white" />
                  )}
                {uploadingPhoto && (
                  <div className="absolute inset-0 bg-black/50 rounded-xl sm:rounded-2xl md:rounded-3xl flex items-center justify-center pointer-events-none">
                    <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-white border-t-transparent"></div>
                  </div>
                )}
              </button>
              <div className="flex-1 min-w-0">
                <h1 className="text-base sm:text-lg md:text-2xl lg:text-3xl font-bold text-gray-900 truncate">{profile.name}</h1>
                <p className="text-[10px] sm:text-xs md:text-sm text-gray-600 truncate mt-0.5">{profile.email}</p>
                <div className="flex items-center mt-1 sm:mt-2 gap-1.5 sm:gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-1.5 sm:px-2 md:px-2.5 py-0.5 sm:py-1 rounded-full text-[9px] sm:text-[10px] md:text-xs font-bold border-2 ${
                    profile.isActive
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-red-50 text-red-700 border-red-200'
                  }`}>
                    {profile.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                  <span className="text-[9px] sm:text-[10px] md:text-xs text-gray-600 font-semibold capitalize">
                    {profile.type === 'store_owner' ? 'Dueño de Negocio' :
                     profile.type === 'store_admin' ? 'Administrador de Negocio' :
                     profile.type === 'customer' ? 'Cliente' :
                     profile.type === 'dispatcher' ? 'Despachador' :
                     profile.type === 'chef' ? 'Cocinero' :
                     profile.type === 'delivery' ? 'Repartidor' :
                     profile.type === 'platform_admin' ? 'Administrador de Plataforma' :
                     (profile.type ? String(profile.type).replace('_', ' ') : 'Usuario')}
                  </span>
                </div>
              </div>
                </div>
              </div>
            </div>
          </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-5 md:py-6">
        {/* Header Card - Compacto */}
        <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 mb-6 overflow-hidden">

          {/* Tabs - Mejorado */}
          <div className="border-b-2 border-gray-200/60 bg-gradient-to-r from-gray-50/50 to-white">
            <nav className="flex space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 overflow-x-auto scrollbar-hide">
              <button
                onClick={() => setActiveTab('profile')}
                className={`py-2 sm:py-2.5 md:py-3 px-2 sm:px-3 md:px-4 rounded-t-lg sm:rounded-t-xl md:rounded-t-2xl font-bold text-[10px] sm:text-xs md:text-sm transition-all duration-200 whitespace-nowrap flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0 ${
                  activeTab === 'profile'
                    ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg'
                    : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100/50'
                }`}
              >
                <UserIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                <span>Información</span>
              </button>

              {profile.type === 'customer' && (
                <button
                  onClick={() => setActiveTab('orders')}
                  className={`py-2 sm:py-2.5 md:py-3 px-2 sm:px-3 md:px-4 rounded-t-lg sm:rounded-t-xl md:rounded-t-2xl font-bold text-[10px] sm:text-xs md:text-sm transition-all duration-200 whitespace-nowrap flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0 ${
                    activeTab === 'orders'
                      ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100/50'
                  }`}
                >
                  <ShoppingBag className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">Mis Pedidos</span>
                  <span className="sm:hidden">Pedidos</span>
                </button>
              )}

              {profile.type === 'store_owner' && (profile.store || (profile.stores && profile.stores.length > 0)) && (
                <button
                  onClick={() => setActiveTab('store')}
                  className={`py-2 sm:py-2.5 md:py-3 px-2 sm:px-3 md:px-4 rounded-t-lg sm:rounded-t-xl md:rounded-t-2xl font-bold text-[10px] sm:text-xs md:text-sm transition-all duration-200 whitespace-nowrap flex items-center gap-1 sm:gap-1.5 md:gap-2 flex-shrink-0 ${
                    activeTab === 'store'
                      ? 'bg-gradient-to-br from-primary-500 to-primary-600 text-white shadow-lg'
                      : 'text-gray-600 hover:text-primary-600 hover:bg-gray-100/50'
                  }`}
                >
                  <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4 md:h-5 md:w-5" />
                  <span className="hidden sm:inline">Mis Negocios</span>
                  <span className="sm:hidden">Negocios</span>
                </button>
              )}
            </nav>
          </div>

          {/* Modo de Vista Toggle - Solo para usuarios que pueden ser empleados */}
          {(isEmployee || (authUser && authUser.type !== 'customer' && authUser.type !== 'platform_admin')) && (
            <div className="px-3 sm:px-4 md:px-6 py-3 sm:py-4 border-t border-gray-200/50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-gray-600 flex-shrink-0" />
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-900">Modo de Vista</p>
                    <p className="text-[10px] sm:text-xs text-gray-500">Cambia entre modo cliente y empleado</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto">
                  <button
                    onClick={() => setViewMode('customer')}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-sm font-medium flex-1 sm:flex-initial justify-center ${
                      viewMode === 'customer'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <UserCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Cliente</span>
                  </button>
                  <button
                    onClick={() => setViewMode('employee')}
                    className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-sm font-medium flex-1 sm:flex-initial justify-center ${
                      viewMode === 'employee'
                        ? 'bg-primary-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Briefcase className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                    <span>Empleado</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <>
              {/* Install PWA Banner */}
              <div className="px-4 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 border-b border-gray-200/60">
                <InstallPWA />
              </div>
            <div className="animate-slide-up">
              <div className="px-4 py-5 sm:p-6">
                <div className="mb-8">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6">Información Personal</h2>

                  {!editing && !changingPassword && (
                    <div className="space-y-6">
                      {/* Sección de Acciones Principales - Compacta y Moderna */}
                      <div className="bg-gradient-to-br from-white to-gray-50/50 rounded-2xl sm:rounded-3xl p-4 sm:p-6 shadow-lg border-2 border-gray-200/60">
                        <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide mb-3 sm:mb-4">Acciones Rápidas</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                          {/* Botón Editar Perfil */}
                          <button
                            onClick={handleEdit}
                            className="group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-md border-2 border-gray-200/60 hover:shadow-xl hover:border-primary-300 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                                <Edit3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                </div>
                              <div className="text-left flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-bold text-gray-900">Editar Perfil</p>
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Actualizar información</p>
                              </div>
                            </div>
                          </button>

                          {/* Botón Cambiar Contraseña */}
                          <button
                            onClick={() => setChangingPassword(true)}
                            className="group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-md border-2 border-gray-200/60 hover:shadow-xl hover:border-amber-300 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 active:scale-95"
                          >
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                  </svg>
                                </div>
                              <div className="text-left flex-1 min-w-0">
                                <p className="text-xs sm:text-sm font-bold text-gray-900">Cambiar Contraseña</p>
                                <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Actualizar seguridad</p>
                              </div>
                            </div>
                          </button>

                          {/* Botón Eliminar Foto (solo si hay foto) */}
                          {profile.profilePhotoUrl && (
                            <button
                              onClick={handleDeletePhoto}
                              disabled={uploadingPhoto}
                              className="group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-4 shadow-md border-2 border-gray-200/60 hover:shadow-xl hover:border-red-300 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-red-500 to-red-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                                  <Trash2 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                  </div>
                                <div className="text-left flex-1 min-w-0">
                                  <p className="text-xs sm:text-sm font-bold text-gray-900">Eliminar Foto</p>
                                  <p className="text-[10px] sm:text-xs text-gray-500 mt-0.5">Quitar foto de perfil</p>
                                </div>
                              </div>
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Sección de Solicitud de Dueño (solo para customers) */}
                      {profile.type === 'customer' && (
                        <div className="space-y-4">
                          <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 rounded-2xl p-6 shadow-sm border border-primary-200/50">
                            <h3 className="text-sm font-semibold text-primary-700 uppercase tracking-wide mb-4">Convertirte en Dueño</h3>
                            <button
                              onClick={handleRequestStoreOwner}
                              disabled={storeOwnerRequest?.status === 'pending'}
                              className="group relative w-full bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 rounded-2xl p-4 shadow-ios text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div className="flex items-center justify-center">
                                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shadow-sm mr-3">
                                  <Store className="h-5 w-5 text-white" />
                                </div>
                                <div className="text-left flex-1">
                                  <p className="text-sm font-semibold">
                                    {storeOwnerRequest?.status === 'pending' ? 'Solicitud pendiente de revisión' :
                                     'Solicitar ser Dueño de Negocio'}
                                  </p>
                                  <p className="text-xs opacity-90">
                                    {storeOwnerRequest?.status === 'pending' ? 'Te notificaremos cuando sea revisada' :
                                     'Registra tu negocio y comienza a vender'}
                                  </p>
                                </div>
                                {storeOwnerRequest?.status !== 'pending' && (
                                  <div className="text-white/70 group-hover:text-white transition-colors duration-300">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                  </div>
                                )}
                              </div>
                            </button>
                          </div>

                          {/* Sección de Unirse a un Negocio */}
                          <div className="bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-2xl p-6 shadow-sm border border-blue-200/50">
                            <h3 className="text-sm font-semibold text-blue-700 uppercase tracking-wide mb-4">Unirse como Empleado</h3>
                            <button
                              onClick={handleJoinStore}
                              className="group relative w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-2xl p-4 shadow-ios text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 active:scale-95"
                            >
                              <div className="flex items-center justify-center">
                                <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shadow-sm mr-3">
                                  <Users className="h-5 w-5 text-white" />
                                </div>
                                <div className="text-left flex-1">
                                  <p className="text-sm font-semibold">
                                    Unirse a un Negocio
                                  </p>
                                  <p className="text-xs opacity-90">
                                    Ingresa un código de invitación para trabajar en un negocio
                                  </p>
                                </div>
                                <div className="text-white/70 group-hover:text-white transition-colors duration-300">
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                </div>
                              </div>
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Sección de Información Laboral - Solo para empleados */}
                      {(profile.type === 'store_admin' || profile.type === 'dispatcher' || profile.type === 'chef' || profile.type === 'delivery' || profile.employee_info) && (
                        <div className="bg-gradient-to-br from-blue-50 to-indigo-50/50 rounded-2xl sm:rounded-3xl shadow-lg border-2 border-blue-200/60 overflow-hidden mb-6">
                          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-indigo-600 border-b-2 border-blue-300/60">
                            <div className="flex items-center gap-2 sm:gap-3">
                              <Briefcase className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                              <h3 className="text-xs sm:text-sm font-bold text-white uppercase tracking-wide">Información Laboral</h3>
                            </div>
                          </div>
                          <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-3 sm:space-y-4">
                            {profile.employee_info ? (
                              <>
                                <div className="flex items-start gap-3 sm:gap-4">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
                                    <Store className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Negocio</p>
                                    <p className="text-sm sm:text-base font-bold text-gray-900 truncate">
                                      {profile.employee_info?.store_name || 'Sin asignar'}
                                    </p>
                                    {profile.employee_info?.store_slug && (
                                      <button
                                        onClick={() => navigate(`/stores/${profile.employee_info?.store_id || ''}`)}
                                        className="text-xs sm:text-sm text-blue-600 hover:text-blue-700 font-medium mt-1"
                                      >
                                        Ver negocio →
                                      </button>
                                    )}
                                  </div>
                                </div>
                                
                                <div className="flex items-start gap-3 sm:gap-4">
                                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
                                    <Users className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Cargo</p>
                                    <p className="text-sm sm:text-base font-bold text-gray-900">
                                      {profile.employee_info?.employee_type === 'store_admin' ? 'Administrador de Negocio' :
                                       profile.employee_info?.employee_type === 'dispatcher' ? 'Despachador' :
                                       profile.employee_info?.employee_type === 'chef' ? 'Cocinero' :
                                       profile.employee_info?.employee_type === 'delivery' ? 'Repartidor' :
                                       profile.type === 'store_admin' ? 'Administrador de Negocio' :
                                       profile.type === 'dispatcher' ? 'Despachador' :
                                       profile.type === 'chef' ? 'Cocinero' :
                                       profile.type === 'delivery' ? 'Repartidor' :
                                       'Empleado'}
                                    </p>
                                  </div>
                                </div>

                                {profile.employee_info?.hired_at && (
                                  <div className="flex items-start gap-3 sm:gap-4">
                                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md flex-shrink-0">
                                      <History className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs sm:text-sm font-semibold text-gray-700 mb-1">Fecha de Contratación</p>
                                      <p className="text-sm sm:text-base font-bold text-gray-900">
                                        {new Date(profile.employee_info.hired_at).toLocaleDateString('es-DO', {
                                          year: 'numeric',
                                          month: 'long',
                                          day: 'numeric'
                                        })}
                                      </p>
                                    </div>
                                  </div>
                                )}

                                <div className="flex items-center gap-2 sm:gap-3 pt-2 sm:pt-3 border-t border-blue-200/60">
                                  <div className={`w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full ${
                                    profile.employee_info?.is_active !== false ? 'bg-green-500' : 'bg-red-500'
                                  }`}></div>
                                  <p className="text-xs sm:text-sm font-medium text-gray-700">
                                    Estado: <span className={`font-bold ${
                                      profile.employee_info?.is_active !== false ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                      {profile.employee_info?.is_active !== false ? 'Activo' : 'Inactivo'}
                                    </span>
                                  </p>
                                </div>
                              </>
                            ) : (
                              <div className="text-center py-4 sm:py-6">
                                <p className="text-sm sm:text-base text-gray-600">
                                  {profile.type === 'delivery' ? 'Repartidor' :
                                   profile.type === 'store_admin' ? 'Administrador' :
                                   profile.type === 'dispatcher' ? 'Despachador' :
                                   profile.type === 'chef' ? 'Cocinero' :
                                   'Empleado'} sin negocio asignado
                                </p>
                                {profile.storeId && (
                                  <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                    Store ID: {profile.storeId}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Sección de Configuración - Compacta */}
                      <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 overflow-hidden">
                        <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b-2 border-gray-200/60">
                          <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide">Configuración</h3>
                        </div>
                        <div className="divide-y-2 divide-gray-200/60">
                          {/* Opción de Tema */}
                          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200 cursor-pointer group">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs sm:text-sm font-bold text-gray-900">Tema</p>
                                <p className="text-[10px] sm:text-xs text-gray-500">Próximamente</p>
                              </div>
                            </div>
                            <div className="text-gray-400 group-hover:text-purple-500 transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>

                          {/* Opción de Notificaciones */}
                          <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200 cursor-pointer group">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-md group-hover:scale-110 transition-transform duration-300">
                                <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.868 12.683A17.925 17.925 0 012 21h6.586l1.707-1.707a2 2 0 012.828 0L13.414 21H20a2 2 0 002-2V8.414a2 2 0 00-.586-1.414l-4-4A2 2 0 0015.586 3H6a2 2 0 00-2 2v6.1a2 2 0 01-.293.707l-.707.707z" />
                                </svg>
                              </div>
                              <div>
                                <p className="text-xs sm:text-sm font-bold text-gray-900">Notificaciones</p>
                                <p className="text-[10px] sm:text-xs text-gray-500">Próximamente</p>
                              </div>
                            </div>
                            <div className="text-gray-400 group-hover:text-green-500 transition-colors">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>

                     
                    </div>
                  )}
                </div>

                {editing && (
                  <div className="bg-white rounded-2xl shadow-ios border border-gray-200/50 p-6 mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-6">Editar Información</h3>
                    <div className="space-y-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nombre completo</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                          className="input-ios w-full"
                          placeholder="Ingresa tu nombre completo"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Teléfono</label>
                        <input
                          type="tel"
                          value={editForm.phone}
                          onChange={(e) => setEditForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="input-ios w-full"
                          placeholder="Ingresa tu número de teléfono"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button
                          onClick={handleSaveProfile}
                          disabled={saving}
                          className="flex-1 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white font-semibold py-3 px-6 rounded-xl shadow-ios hover:shadow-ios-hover transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="h-5 w-5 mr-2 inline" />
                          <span>{saving ? 'Guardando...' : 'Guardar Cambios'}</span>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 active:scale-95"
                        >
                          <X className="h-5 w-5 mr-2 inline" />
                          <span>Cancelar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {changingPassword && (
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200/50 rounded-2xl shadow-sm p-6 mb-6">
                    <div className="flex items-center mb-6">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center shadow-sm">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-lg font-semibold text-amber-800">Cambiar Contraseña</h3>
                        <p className="text-sm text-amber-600">Actualiza tu contraseña de forma segura</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Contraseña actual</label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={passwordData.currentPassword}
                            onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                            className="input-ios w-full pr-12"
                            placeholder="Ingresa tu contraseña actual"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute inset-y-0 right-0 pr-4 flex items-center hover:bg-gray-100 rounded-r-xl transition-colors duration-200"
                          >
                            {showPassword ? <EyeOff className="h-5 w-5 text-gray-400" /> : <Eye className="h-5 w-5 text-gray-400" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Nueva contraseña</label>
                        <input
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                          className="input-ios w-full"
                          placeholder="Ingresa tu nueva contraseña"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Confirmar nueva contraseña</label>
                        <input
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="input-ios w-full"
                          placeholder="Confirma tu nueva contraseña"
                        />
                      </div>
                      <div className="flex flex-col sm:flex-row gap-4 pt-4">
                        <button
                          onClick={handleChangePassword}
                          disabled={saving}
                          className="flex-1 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold py-3 px-6 rounded-xl shadow-ios hover:shadow-ios-hover transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <Save className="h-5 w-5 mr-2 inline" />
                          <span>{saving ? 'Cambiando...' : 'Cambiar Contraseña'}</span>
                        </button>
                        <button
                          onClick={() => {
                            setChangingPassword(false)
                            setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
                          }}
                          className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-xl border border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 active:scale-95"
                        >
                          <X className="h-5 w-5 mr-2 inline" />
                          <span>Cancelar</span>
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {!editing && !changingPassword && (
                  <div className="space-y-4 sm:space-y-6">
                    {/* Información Personal - Compacta */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b-2 border-gray-200/60">
                        <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide">Información Personal</h3>
                      </div>
                      <div className="divide-y-2 divide-gray-200/60">
                        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 hover:bg-gray-50/50 transition-colors duration-200">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                            <Mail className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-bold text-gray-900">Correo electrónico</p>
                            <p className="text-[10px] sm:text-xs text-gray-600 break-words mt-0.5">{profile.email}</p>
                          </div>
                        </div>

                        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 hover:bg-gray-50/50 transition-colors duration-200">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                            <Phone className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-bold text-gray-900">Teléfono</p>
                            <p className="text-[10px] sm:text-xs text-gray-600 break-words mt-0.5">{profile.phone || 'No especificado'}</p>
                          </div>
                        </div>

                        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-3 hover:bg-gray-50/50 transition-colors duration-200">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                              <UserIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-xs sm:text-sm font-bold text-gray-900">Tipo de usuario</p>
                              <p className="text-[10px] sm:text-xs text-gray-600 capitalize mt-0.5">
                                {profile.type === 'store_owner' ? 'Dueño de Negocio' :
                                 profile.type === 'store_admin' ? 'Administrador de Negocio' :
                                 profile.type === 'customer' ? 'Cliente' :
                                 profile.type === 'dispatcher' ? 'Despachador' :
                                 profile.type === 'chef' ? 'Cocinero' :
                                 profile.type === 'delivery' ? 'Repartidor' :
                                 profile.type === 'platform_admin' ? 'Administrador de Plataforma' :
                                 String(profile.type || '').replace('_', ' ')}
                              </p>
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <span className={`inline-flex items-center px-2 sm:px-2.5 py-1 rounded-full text-[10px] sm:text-xs font-bold border-2 shadow-sm ${profile.isActive ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                              {profile.isActive ? 'Activo' : 'Inactivo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Información de Cuenta - Compacta */}
                    <div className="bg-white rounded-2xl sm:rounded-3xl shadow-lg border-2 border-gray-200/60 overflow-hidden">
                      <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gradient-to-r from-gray-50 to-gray-100/50 border-b-2 border-gray-200/60">
                        <h3 className="text-xs sm:text-sm font-bold text-gray-700 uppercase tracking-wide">Cuenta</h3>
                      </div>
                      <div className="divide-y-2 divide-gray-200/60">
                        <div className="px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 hover:bg-gray-50/50 transition-colors duration-200">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                            <svg className="h-5 w-5 sm:h-6 sm:w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs sm:text-sm font-bold text-gray-900">Fecha de registro</p>
                            <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5">{new Date(profile.createdAt).toLocaleDateString('es-DO', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                          </div>
                        </div>

                        {profile.type === 'customer' && storeOwnerRequest && (
                          <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                <Store className="h-4 w-4 text-white" />
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900">Solicitud de Dueño</p>
                                <p className="text-xs text-gray-500">
                                  {storeOwnerRequest.status === 'pending' && `Solicitado el ${new Date(storeOwnerRequest.requested_at).toLocaleDateString('es-ES')}`}
                                </p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-3">
                              <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm ${storeOwnerRequest.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : storeOwnerRequest.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                {storeOwnerRequest.status === 'pending' ? 'Pendiente' : storeOwnerRequest.status === 'approved' ? 'Aprobada' : 'Rechazada'}
                              </span>
                            </div>
                          </div>
                        )}

                        {profile.type === 'store_owner' && (
                          <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                <Building2 className="h-4 w-4 text-white" />
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900">Mis Negocios</p>
                                <p className="text-xs text-gray-500">{profile.stores?.length || 0} negocios activos</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-3">
                              <button
                                onClick={() => navigate('/store-owner/my-stores')}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 shadow-sm hover:bg-emerald-200 transition-colors"
                              >
                                {profile.stores?.length || 0}
                              </button>
                            </div>
                          </div>
                        )}

                        {['dispatcher', 'chef', 'delivery', 'store_admin'].includes(profile.type) && profile.storeId && (
                          <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                <Store className="h-4 w-4 text-white" />
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900">Negocio Asignado</p>
                                <p className="text-xs text-gray-500">ID: {profile.storeId}</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-3">
                              <button
                                onClick={() => navigate(`/store/${profile.storeId}/dashboard`)}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800 shadow-sm hover:bg-blue-200 transition-colors"
                              >
                                Ver
                              </button>
                            </div>
                          </div>
                        )}

                        {profile.type === 'platform_admin' && (
                          <div className="px-6 py-4 flex items-center justify-between hover:bg-gray-50/50 transition-colors duration-200">
                            <div className="flex items-center flex-1 min-w-0">
                              <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center shadow-sm flex-shrink-0">
                                <Building2 className="h-4 w-4 text-white" />
                              </div>
                              <div className="ml-3 min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900">Panel de Administración</p>
                                <p className="text-xs text-gray-500">Acceso completo a la plataforma</p>
                              </div>
                            </div>
                            <div className="flex-shrink-0 ml-3">
                              <button
                                onClick={() => navigate('/admin/dashboard')}
                                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-800 shadow-sm hover:bg-purple-200 transition-colors"
                              >
                                Ir
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                     {/* Sección de Privacidad y Datos - iOS Style */}
                     <div className="bg-gradient-to-br from-blue-50/80 via-blue-50/60 to-indigo-50/80 backdrop-blur-sm rounded-3xl p-6 sm:p-7 shadow-ios border border-blue-200/40 mb-5">
                        {/* Header */}
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg shadow-blue-500/20">
                            <Shield className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <h3 className="text-xl font-bold text-gray-900">
                              Privacidad y Datos
                            </h3>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Gestiona tu información personal
                            </p>
                          </div>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-600 mb-6 leading-relaxed px-1">
                          Ejercita tus derechos según la Ley 172-13 de Protección de Datos Personales de República Dominicana.
                        </p>

                        {/* Action Buttons - iOS Style */}
                        <div className="space-y-3 mb-5">
                          <button
                            onClick={async () => {
                              if (window.confirm('¿Deseas exportar todos tus datos personales? Se descargará un archivo PDF con toda tu información.')) {
                                try {
                                  setSaving(true)
                                  await api.exportUserData()
                                  toast.success('PDF generado exitosamente. Revisa tu carpeta de descargas.')
                                } catch (error: any) {
                                  console.error('Error exporting data:', error)
                                  toast.error(error.response?.data?.message || 'Error al exportar datos')
                                } finally {
                                  setSaving(false)
                                }
                              }
                            }}
                            disabled={saving}
                            className="group relative w-full px-5 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl font-semibold hover:from-blue-700 hover:to-blue-800 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-blue-500/30 hover:shadow-xl hover:shadow-blue-500/40 min-h-[52px]"
                          >
                            <Download className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span>Exportar Mis Datos</span>
                          </button>
                          <button
                            onClick={() => {
                              const confirmation = window.prompt(
                                '⚠️ ADVERTENCIA: Esta acción es IRREVERSIBLE.\n\n' +
                                'Se eliminará permanentemente:\n' +
                                '- Tu cuenta y perfil\n' +
                                '- Todas tus direcciones\n' +
                                '- Tu historial de pedidos\n' +
                                '- Tus fotos de perfil\n\n' +
                                'Escribe "ELIMINAR MI CUENTA" para confirmar:'
                              )
                              
                              if (confirmation === 'ELIMINAR MI CUENTA') {
                                if (window.confirm('¿Estás completamente seguro? Esta acción NO se puede deshacer.')) {
                                  handleDeleteAccount(confirmation)
                                }
                              } else if (confirmation !== null) {
                                toast.error('Confirmación incorrecta. La cuenta no fue eliminada.')
                              }
                            }}
                            disabled={saving}
                            className="group relative w-full px-5 py-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-2xl font-semibold hover:from-red-600 hover:to-red-700 active:scale-95 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-red-500/30 hover:shadow-xl hover:shadow-red-500/40 min-h-[52px]"
                          >
                            <Trash2 className="w-5 h-5 group-hover:scale-110 transition-transform" />
                            <span>Eliminar Mi Cuenta</span>
                          </button>
                        </div>

                        {/* Footer Link */}
                        <div className="pt-4 border-t border-blue-200/50">
                          <Link
                            to="/privacy"
                            className="group flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium transition-colors"
                          >
                            <FileText className="w-4 h-4 group-hover:scale-110 transition-transform" />
                            <span>Ver Política de Privacidad</span>
                          </Link>
                        </div>
                     </div>

                     {/* Botón de Cerrar Sesión - Diseño especial iOS */}
                     <div className="bg-gradient-to-br from-red-50 to-red-100/50 rounded-2xl p-6 shadow-sm border border-red-200/50">
                        <button
                          onClick={() => {
                            if (window.confirm('¿Estás seguro de que quieres cerrar sesión?')) {
                              localStorage.removeItem('token')
                              localStorage.removeItem('user')
                              window.location.href = '/'
                            }
                          }}
                          className="group relative w-full bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-2xl p-4 shadow-ios text-white transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 active:scale-95"
                        >
                          <div className="flex items-center justify-center">
                            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shadow-sm mr-3">
                              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                              </svg>
                            </div>
                            <div className="text-left flex-1">
                              <p className="text-sm font-semibold">Cerrar Sesión</p>
                              <p className="text-xs opacity-90">Salir de tu cuenta</p>
                            </div>
                            <div className="text-white/70 group-hover:text-white transition-colors duration-300">
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                              </svg>
                            </div>
                          </div>
                        </button>
                      </div>
                  </div>
                )}
              </div>
            </div>
            </>
          )}

          {/* Orders Tab */}
          {activeTab === 'orders' && profile.type === 'customer' && (
            <div className="bg-white shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h2 className="text-lg font-medium text-gray-900 mb-6">Mis Pedidos</h2>

                {orders.length === 0 ? (
                  <div className="text-center py-12">
                    <ShoppingBag className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No hay pedidos</h3>
                    <p className="mt-1 text-sm text-gray-500">Aún no has realizado ningún pedido.</p>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate('/')}
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                      >
                        Explorar Negocios
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-4">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                Pedido #{order.id.slice(-8)}
                              </p>
                              <p className="text-sm text-gray-500">
                                {new Date(order.createdAt).toLocaleDateString('es-ES')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                            <span className="text-sm font-medium text-gray-900">
                              RD$ {((order.totalAmount || 0)).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Store Tab */}
          {activeTab === 'store' && profile.type === 'store_owner' && (profile.store || (profile.stores && profile.stores.length > 0)) && (
            <div className="space-y-6">
              <div className="bg-white/60 backdrop-blur-xl rounded-xl sm:rounded-2xl shadow-xl border border-white/20 p-6 sm:p-8">
                <h2 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-6 sm:mb-8">
                  {profile.stores && profile.stores.length > 1 ? 'Mis Negocios' : 'Mi Negocio'}
                </h2>

                {profile.stores && profile.stores.length > 0 ? (
                  <div className="space-y-4 sm:space-y-6">
                    {profile.stores.map((store: any) => (
                      <div key={store.id} className="group bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/30 shadow-lg hover:shadow-xl hover:bg-white/60 transition-all duration-300 hover:-translate-y-1 overflow-hidden">
                        <div className="p-6 sm:p-8">
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-6 mb-6">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-4">
                                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                                  <Store className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 truncate mb-2">{store.name}</h3>
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className={`inline-flex items-center px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold ${
                                      store.status === 'approved'
                                        ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50'
                                        : store.status === 'pending'
                                        ? 'bg-amber-50/80 text-amber-700 border border-amber-200/50'
                                        : 'bg-red-50/80 text-red-700 border border-red-200/50'
                                    }`}>
                                      {store.status === 'approved' ? 'Aprobado' :
                                       store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                                    </span>
                                    <span className={`inline-flex items-center px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold ${
                                      store.isActive
                                        ? 'bg-green-50/80 text-green-700 border border-green-200/50'
                                        : 'bg-red-50/80 text-red-700 border border-red-200/50'
                                    }`}>
                                      {store.isActive ? 'Activo' : 'Inactivo'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {store.address && (
                                <div className="flex items-start gap-3 mt-4">
                                  <MapPin className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                                  <p className="text-sm sm:text-base text-gray-600 font-medium">{store.address}</p>
                                </div>
                              )}
                            </div>
                            <button
                              onClick={() => navigate(`/store/${store.id}/dashboard`)}
                              className="w-full sm:w-auto px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 flex items-center justify-center gap-2"
                            >
                              <Store className="w-5 h-5" />
                              <span>Gestionar</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : profile.store ? (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/30 shadow-lg p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6">Información General</h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Nombre</dt>
                          <dd className="text-base sm:text-lg font-semibold text-gray-900">{profile.store.name}</dd>
                        </div>
                        <div>
                          <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Estado</dt>
                          <dd>
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold ${
                              profile.store.status === 'approved'
                                ? 'bg-emerald-50/80 text-emerald-700 border border-emerald-200/50'
                                : profile.store.status === 'pending'
                                ? 'bg-amber-50/80 text-amber-700 border border-amber-200/50'
                                : 'bg-red-50/80 text-red-700 border border-red-200/50'
                            }`}>
                              {profile.store.status === 'approved' ? 'Aprobado' :
                               profile.store.status === 'pending' ? 'Pendiente' : 'Rechazado'}
                            </span>
                          </dd>
                        </div>
                        <div>
                          <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Activo</dt>
                          <dd>
                            <span className={`inline-flex items-center px-3 py-1.5 rounded-xl sm:rounded-2xl text-xs sm:text-sm font-bold ${
                              profile.store.isActive
                                ? 'bg-green-50/80 text-green-700 border border-green-200/50'
                                : 'bg-red-50/80 text-red-700 border border-red-200/50'
                            }`}>
                              {profile.store.isActive ? 'Sí' : 'No'}
                            </span>
                          </dd>
                        </div>
                        {profile.store.address && (
                          <div>
                            <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1 flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              Dirección
                            </dt>
                            <dd className="text-base sm:text-lg font-semibold text-gray-900">{profile.store.address}</dd>
                          </div>
                        )}
                      </dl>
                    </div>
                    <div className="bg-white/40 backdrop-blur-sm rounded-xl sm:rounded-2xl border border-white/30 shadow-lg p-6">
                      <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-6">Configuración de Servicio</h3>
                      <dl className="space-y-4">
                        <div>
                          <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Ofrece Entrega</dt>
                          <dd className="text-base sm:text-lg font-semibold text-gray-900">
                            {profile.store.offersDelivery ? 'Sí' : 'No'}
                          </dd>
                        </div>
                        {profile.store.offersDelivery && (
                          <>
                            <div>
                              <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Tarifa de Entrega</dt>
                              <dd className="text-base sm:text-lg font-semibold text-gray-900">
                                RD$ {profile.store.deliveryFee?.toLocaleString() || '0'}
                              </dd>
                            </div>
                            <div>
                              <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Monto Mínimo</dt>
                              <dd className="text-base sm:text-lg font-semibold text-gray-900">
                                RD$ {profile.store.minOrderAmount?.toLocaleString() || '0'}
                              </dd>
                            </div>
                          </>
                        )}
                        <div>
                          <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Ofrece Cocina</dt>
                          <dd className="text-base sm:text-lg font-semibold text-gray-900">
                            {profile.store.offersKitchenService ? 'Sí' : 'No'}
                          </dd>
                        </div>
                        {profile.store.rating != null && (
                          <div>
                            <dt className="text-xs sm:text-sm font-bold text-gray-500 uppercase tracking-wide mb-1">Rating</dt>
                            <dd className="text-base sm:text-lg font-semibold text-gray-900">
                              ⭐ {typeof profile.store.rating === 'number' ? profile.store.rating : 0}/5 ({profile.store.reviewCount || 0} reseñas)
                            </dd>
                          </div>
                        )}
                      </dl>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 sm:py-16">
                    <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl sm:rounded-2xl mb-6 shadow-lg">
                      <Store className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400" />
                    </div>
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">No tienes negocios</h3>
                    <p className="text-sm sm:text-base text-gray-600 mb-8">Crea tu primer negocio para comenzar.</p>
                    <button
                      onClick={() => navigate('/create-store')}
                      className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95"
                    >
                      Crear Negocio
                    </button>
                  </div>
                )}

                {profile.stores && profile.stores.length > 0 && (
                  <div className="mt-8 pt-8 border-t border-white/30">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">Gestión de Negocios</h3>
                        <p className="text-sm sm:text-base text-gray-600">
                          Accede a herramientas para gestionar tus negocios
                        </p>
                      </div>
                      <button
                        onClick={() => navigate('/store-owner/my-stores')}
                        className="px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl sm:rounded-2xl font-semibold shadow-lg hover:shadow-xl transition-all duration-300 active:scale-95 flex items-center gap-2"
                      >
                        <Store className="w-5 h-5" />
                        <span>Ver Todos</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Editor Modal */}
      {showImageEditor && selectedImageFile && (
        <ImageEditor
          image={selectedImageFile}
          onSave={handleSaveEditedImage}
          onCancel={handleCancelImageEdit}
          aspectRatio={1} // Forzar relación de aspecto cuadrada para foto de perfil
        />
      )}

      {/* Photo Options Modal */}
      {showPhotoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-900">Foto de Perfil</h2>
              <button
                onClick={() => setShowPhotoModal(false)}
                className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all duration-150"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Photo Display */}
            <div className="p-6 flex items-center justify-center bg-gradient-to-br from-gray-50 to-white">
              <div className="w-48 h-48 sm:w-64 sm:h-64 rounded-full overflow-hidden bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-2xl ring-4 ring-white">
                {photoSignedUrl || photoPreview ? (
                  <img
                    src={photoPreview || photoSignedUrl || ''}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <UserIcon className="h-24 w-24 sm:h-32 sm:w-32 text-white" />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 border-t border-gray-100 space-y-3">
              <label className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-primary-600 text-white rounded-2xl hover:bg-primary-700 transition-all duration-150 font-semibold cursor-pointer active:scale-95 shadow-lg shadow-primary-500/30">
                <Camera className="w-5 h-5" />
                <span>Cambiar Foto</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  disabled={uploadingPhoto}
                  className="hidden"
                />
              </label>

              {photoSignedUrl && (
                <>
                  <button
                    onClick={() => {
                      setShowPhotoModal(false)
                      handleShowHistory()
                    }}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-gray-100 text-gray-700 rounded-2xl hover:bg-gray-200 transition-all duration-150 font-semibold active:scale-95"
                  >
                    <History className="w-5 h-5" />
                    <span>Ver Historial</span>
                  </button>

                  <button
                    onClick={async () => {
                      if (confirm('¿Estás seguro de que quieres eliminar tu foto de perfil? Esta acción eliminará la foto permanentemente.')) {
                        setShowPhotoModal(false)
                        await handleDeletePhoto()
                      }
                    }}
                    className="w-full flex items-center justify-center gap-3 px-6 py-3.5 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition-all duration-150 font-semibold active:scale-95 shadow-lg shadow-red-500/30"
                    disabled={uploadingPhoto}
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>Eliminar Foto</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo History Modal */}
      {showHistoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
              <h2 className="text-2xl font-semibold text-gray-900">Historial de Fotos</h2>
              <button
                onClick={() => setShowHistoryModal(false)}
                className="p-2.5 hover:bg-gray-100 active:bg-gray-200 rounded-full transition-all duration-150"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6">
              {loadingHistory ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-gray-500">Cargando historial...</div>
                </div>
              ) : photoHistory.length === 0 ? (
                <div className="text-center py-12">
                  <History className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                  <p className="text-gray-500">No hay fotos en el historial</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                  {photoHistory.map((item) => (
                    <div
                      key={item.id}
                      className={`relative group rounded-2xl overflow-hidden border-2 ${
                        item.is_current
                          ? 'border-primary-500 ring-2 ring-primary-200'
                          : 'border-gray-200 hover:border-primary-300'
                      }`}
                    >
                      {item.signedUrl ? (
                        <img
                          src={item.signedUrl}
                          alt="Foto anterior"
                          className="w-full h-32 sm:h-40 object-cover"
                        />
                      ) : (
                        <div className="w-full h-32 sm:h-40 bg-gray-100 flex items-center justify-center">
                          <UserIcon className="h-8 w-8 text-gray-400" />
                        </div>
                      )}
                      {item.is_current && (
                        <div className="absolute top-2 right-2 bg-primary-600 text-white text-xs px-2 py-1 rounded-full">
                          Actual
                        </div>
                      )}
                      {!item.is_current && (
                        <button
                          onClick={() => handleRestorePhoto(item.id)}
                          className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                          disabled={uploadingPhoto}
                        >
                          <div className="bg-white rounded-full p-2">
                            <RotateCcw className="h-5 w-5 text-primary-600" />
                          </div>
                        </button>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <p className="text-white text-xs">
                          {new Date(item.created_at).toLocaleDateString('es-ES', {
                            day: 'numeric',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Profile